import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import VideoCard from "@/components/base/VideoCard";
import { useSeo } from "@/hooks/useSeo";
import { getCategoryById, getVideosByCategory, parseDuration } from "@/utils/video";

type SortKey = "latest" | "views" | "likes";
type DateKey = "all" | "month" | "quarter";
type DurKey = "all" | "short" | "mid" | "long";

const SORTS: { key: SortKey; label: string; icon: string }[] = [
  { key: "latest", label: "最新发布", icon: "ri-time-line" },
  { key: "views", label: "最多播放", icon: "ri-play-circle-line" },
  { key: "likes", label: "最多点赞", icon: "ri-heart-line" },
];

const DATES: { key: DateKey; label: string }[] = [
  { key: "all", label: "全部时间" },
  { key: "month", label: "最近一个月" },
  { key: "quarter", label: "最近三个月" },
];

const DURS: { key: DurKey; label: string }[] = [
  { key: "all", label: "全部时长" },
  { key: "short", label: "10 分钟以内" },
  { key: "mid", label: "10 - 18 分钟" },
  { key: "long", label: "18 分钟以上" },
];

export default function Category() {
  const { categoryId = "" } = useParams();
  const category = getCategoryById(categoryId);

  const [sort, setSort] = useState<SortKey>("latest");
  const [dateKey, setDateKey] = useState<DateKey>("all");
  const [durKey, setDurKey] = useState<DurKey>("all");
  const [tag, setTag] = useState<string>("全部");
  const [visible, setVisible] = useState(6);
  const [mobileFilter, setMobileFilter] = useState(false);

  const baseVideos = useMemo(() => getVideosByCategory(categoryId), [categoryId]);
  const tags = useMemo(
    () => ["全部", ...Array.from(new Set(baseVideos.flatMap((v) => v.tags)))],
    [baseVideos],
  );

  useSeo({
    title: category
      ? `${category.name} - 糖心Vlog分类视频`
      : "视频分类 - 糖心Vlog",
    description: category
      ? `糖心Vlog「${category.name}」分类合集：${category.description} 发现多样的生活体验与真实视频记录。`
      : "浏览糖心Vlog的多元视频分类。",
    keywords: category
      ? `${category.name},糖心Vlog,${category.name}视频,生活视频,真实生活`
      : "糖心Vlog,视频分类",
    path: `/category/${categoryId}`,
  });

  const filtered = useMemo(() => {
    let list = [...baseVideos];
    const now = Date.now();
    const dayMs = 24 * 60 * 60 * 1000;
    if (dateKey !== "all") {
      const span = dateKey === "month" ? 30 : 90;
      list = list.filter((v) => now - new Date(v.publishedAt).getTime() <= span * dayMs);
    }
    if (durKey !== "all") {
      list = list.filter((v) => {
        const sec = parseDuration(v.duration);
        if (durKey === "short") return sec < 600;
        if (durKey === "mid") return sec >= 600 && sec < 1080;
        return sec >= 1080;
      });
    }
    if (tag !== "全部") {
      list = list.filter((v) => v.tags.includes(tag));
    }
    list.sort((a, b) => {
      if (sort === "views") return b.views - a.views;
      if (sort === "likes") return b.likes - a.likes;
      return a.publishedAt < b.publishedAt ? 1 : -1;
    });
    return list;
  }, [baseVideos, dateKey, durKey, tag, sort]);

  const resetFilters = () => {
    setSort("latest");
    setDateKey("all");
    setDurKey("all");
    setTag("全部");
    setVisible(6);
  };

  if (!category) {
    return (
      <div className="mx-auto flex max-w-7xl flex-col items-center px-4 py-24 text-center md:px-8">
        <i className="ri-error-warning-line text-5xl text-foreground-400" />
        <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950">分类不存在</h1>
        <p className="mt-2 text-sm text-foreground-600">没有找到对应的视频分类。</p>
        <Link
          to="/"
          className="mt-6 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
        >
          返回首页
        </Link>
      </div>
    );
  }

  const shown = filtered.slice(0, visible);

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 md:px-8 md:py-12">
      {/* breadcrumb */}
      <nav className="flex flex-wrap items-center gap-2 text-sm text-foreground-500">
        <Link to="/" className="transition-colors hover:text-primary-600">
          首页
        </Link>
        <i className="ri-arrow-right-s-line" />
        <span className="text-foreground-500">视频分类</span>
        <i className="ri-arrow-right-s-line" />
        <span className="font-medium text-foreground-900">{category.name}</span>
      </nav>

      {/* header */}
      <div className="mt-6 flex items-start gap-4 rounded-lg border border-background-200 bg-background-100/60 p-6 md:p-8">
        <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-lg bg-primary-500 text-3xl text-background-50">
          <i className={category.icon} />
        </span>
        <div>
          <h1 className="font-heading text-2xl font-extrabold text-foreground-950 md:text-3xl">
            {category.name}
          </h1>
          <p className="mt-2 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
            {category.description}
          </p>
        </div>
      </div>

      {/* toolbar */}
      <div className="mt-5 flex flex-col gap-4 rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-foreground-700">排序：</span>
          {SORTS.map((s) => (
            <button
              key={s.key}
              type="button"
              onClick={() => {
                setSort(s.key);
                setVisible(6);
              }}
              className={`flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md px-3 py-1.5 text-sm transition-colors ${
                sort === s.key
                  ? "bg-primary-500 font-medium text-background-50"
                  : "bg-background-100 text-foreground-600 hover:bg-background-200"
              }`}
            >
              <i className={s.icon} /> {s.label}
            </button>
          ))}
          <button
            type="button"
            onClick={() => setMobileFilter((v) => !v)}
            className="ml-auto flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-3 py-1.5 text-sm text-foreground-700 lg:hidden"
          >
            <i className="ri-filter-3-line" /> 筛选
          </button>
        </div>

        <div className={`${mobileFilter ? "flex" : "hidden lg:flex"} flex-col gap-3 border-t border-background-200 pt-3`}>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium text-foreground-700">发布：</span>
            {DATES.map((d) => (
              <button
                key={d.key}
                type="button"
                onClick={() => {
                  setDateKey(d.key);
                  setVisible(6);
                }}
                className={`cursor-pointer whitespace-nowrap rounded-full px-3 py-1 text-sm transition-colors ${
                  dateKey === d.key
                    ? "bg-accent-100 font-medium text-accent-800"
                    : "bg-background-100 text-foreground-600 hover:bg-background-200"
                }`}
              >
                {d.label}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium text-foreground-700">时长：</span>
            {DURS.map((d) => (
              <button
                key={d.key}
                type="button"
                onClick={() => {
                  setDurKey(d.key);
                  setVisible(6);
                }}
                className={`cursor-pointer whitespace-nowrap rounded-full px-3 py-1 text-sm transition-colors ${
                  durKey === d.key
                    ? "bg-accent-100 font-medium text-accent-800"
                    : "bg-background-100 text-foreground-600 hover:bg-background-200"
                }`}
              >
                {d.label}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium text-foreground-700">标签：</span>
            {tags.map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => {
                  setTag(t);
                  setVisible(6);
                }}
                className={`cursor-pointer whitespace-nowrap rounded-full px-3 py-1 text-sm transition-colors ${
                  tag === t
                    ? "bg-secondary-500 font-medium text-background-50"
                    : "bg-secondary-100 text-secondary-900 hover:bg-secondary-200"
                }`}
              >
                {t}
              </button>
            ))}
          </div>
        </div>
      </div>

      <p className="mt-5 text-sm text-foreground-500">
        共找到 <span className="font-semibold text-foreground-900">{filtered.length}</span> 个视频
      </p>

      {/* list */}
      {shown.length === 0 ? (
        <div className="mt-6 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-20 text-center">
          <i className="ri-emotion-sad-line text-5xl text-foreground-400" />
          <h2 className="mt-4 font-heading text-xl font-bold text-foreground-950">
            当前分类暂无相关视频
          </h2>
          <p className="mt-2 text-sm text-foreground-600">
            试试调整筛选条件，或换个角度看其他内容。
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <button
              type="button"
              onClick={resetFilters}
              className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              重置筛选
            </button>
            <Link
              to="/"
              className="whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300"
            >
              返回首页
            </Link>
          </div>
        </div>
      ) : (
        <>
          <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {shown.map((v, i) => (
              <VideoCard key={v.id} video={v} index={i} />
            ))}
          </div>

          {visible < filtered.length && (
            <div className="mt-10 flex justify-center">
              <button
                type="button"
                onClick={() => setVisible((n) => n + 6)}
                className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-6 py-3 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-600"
              >
                <i className="ri-refresh-line" /> 加载更多视频
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}